import React, { useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const EMPTY = { city: "", province: "", region: "", dealer_name: "", address: "", latitude: 0, longitude: 0, telephone: "", opening_hours: "", test_drive_days: "", local_description: "", accessibility: "", directions: "", parking: "", indexable: false };

export default function LocationsAdmin() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data: locations = [], isLoading } = useQuery({ queryKey: ["admin-locations"], queryFn: () => api.get("/admin/locations").then((r) => r.data) });

  const create = useMutation({ mutationFn: (b) => api.post("/admin/locations", b).then((r) => r.data), onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-locations"] }); toast.success("Località creata"); setEditing(null); }, onError: () => toast.error("Errore") });
  const update = useMutation({ mutationFn: ({ id, b }) => api.patch(`/admin/locations/${id}`, b).then((r) => r.data), onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-locations"] }); toast.success("Aggiornata"); setEditing(null); }, onError: () => toast.error("Errore") });

  const save = () => {
    const b = { ...editing, latitude: parseFloat(editing.latitude) || 0, longitude: parseFloat(editing.longitude) || 0, status: editing.indexable ? "PUBLISHED" : "DRAFT" };
    if (editing.id) update.mutate({ id: editing.id, b }); else create.mutate(b);
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-2">
        <div><h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Pagine Città (Local SEO)</h1><p className="text-slate-500">{locations.length} località</p></div>
        <Button onClick={() => setEditing({ ...EMPTY })} className="font-bold text-white rounded-full" style={{ backgroundColor: "var(--sc-emerald)" }} data-testid="add-location">+ Aggiungi</Button>
      </div>
      <p className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3 mb-6">Le pagine restano <b>noindex</b> finché non attivi "Indicizzabile": pubblicale solo quando i contenuti locali sono reali e completi.</p>

      {isLoading ? <p className="text-slate-500">Caricamento...</p> : (
        <div className="grid md:grid-cols-2 gap-4">
          {locations.map((l) => (
            <div key={l.id} className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={`admin-location-${l.slug}`}>
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-slate-900">Prova SENIORCAR {l.city}</h3>
                  <p className="text-slate-500 text-sm">/prova-seniorcar/{l.slug}</p>
                  <span className={`inline-block mt-2 text-xs font-bold px-2.5 py-1 rounded-full ${l.indexable ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>{l.indexable ? "Indicizzabile" : "Noindex (bozza)"}</span>
                </div>
                <Button size="sm" variant="outline" onClick={() => setEditing(l)} data-testid={`edit-location-${l.slug}`}>Modifica</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {editing && (
            <>
              <DialogHeader><DialogTitle>{editing.id ? "Modifica" : "Nuova"} pagina città</DialogTitle><DialogDescription>Contenuti locali reali e coordinate. Indicizza solo quando è completa.</DialogDescription></DialogHeader>
              <div className="space-y-3">
                {[["city", "Città"], ["province", "Provincia"], ["region", "Regione"], ["dealer_name", "Centro/Rivenditore"], ["address", "Indirizzo"], ["latitude", "Latitudine"], ["longitude", "Longitudine"], ["telephone", "Telefono"], ["opening_hours", "Orari"], ["test_drive_days", "Giorni prova"]].map(([k, l]) => (
                  <div key={k}><label className="text-sm font-semibold text-slate-700">{l}</label><Input value={editing[k] ?? ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} className="mt-1" data-testid={`location-${k}`} /></div>
                ))}
                {[["local_description", "Descrizione locale"], ["accessibility", "Accessibilità"], ["directions", "Come arrivare"], ["parking", "Parcheggio"]].map(([k, l]) => (
                  <div key={k}><label className="text-sm font-semibold text-slate-700">{l}</label><Textarea value={editing[k] ?? ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} rows={2} className="mt-1" /></div>
                ))}
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Switch checked={!!editing.indexable} onCheckedChange={(v) => setEditing({ ...editing, indexable: v })} data-testid="location-indexable" /> Indicizzabile (rimuovi noindex)</label>
                <Button onClick={save} className="w-full h-11 font-bold text-white rounded-full" style={{ backgroundColor: "var(--sc-emerald)" }} data-testid="location-save">Salva</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
